import React, { memo } from 'react';

import { Platform, ViewStyle } from 'react-native';

import Animated, { useAnimatedStyle } from 'react-native-reanimated';

import { isWeb } from 'utils/responsiveness';

import { NormalCellProps } from '../types';
import { useIsInboundAnimated } from '../useIsInboundAnimated';
import NormalCellMobile from './NormalCellMobile';
import NormalCellTV from './NormalCellTV';
import { StickyCellBorderMasking } from './StickyCellBorderMasking';

const NormalCell = (props: NormalCellProps) => {
    const {
        config,
        rect,
        xVisibleStartOffset,
        xVisibleEndOffset,
        yVisibleStartOffset,
        yVisibleEndOffset,
        onSelect,
        onPressTV,
        screenReaderEnabled,
    } = props;

    const inbound = useIsInboundAnimated(
        rect,
        xVisibleStartOffset,
        xVisibleEndOffset,
        yVisibleStartOffset,
        yVisibleEndOffset,
    );

    const animatedStyle = useAnimatedStyle(() => {
        const enabled =
            !isWeb &&
            !screenReaderEnabled &&
            config.stickyPrograms &&
            inbound.value &&
            rect.width >=
                (config.stickyProgramTriggerMins * config.hourWidth) / 60;
        const minShrink =
            (config.stickyProgramReleaseMins * config.hourWidth) / 60;

        // On web this is not smooth, as reanimated runs purely on JS side
        // Consider to adjust text position to sticky on client side.
        let translateX = enabled
            ? Math.max(0, xVisibleStartOffset.value - rect.x - config.padding)
            : 0;
        if (translateX && rect.width - translateX < minShrink) {
            translateX += rect.width - translateX - minShrink;
        }
        return {
            transform: [
                {
                    translateX,
                },
            ],
            width: rect.width,
            marginLeft:
                !isWeb &&
                !screenReaderEnabled &&
                config.stickyPrograms &&
                inbound.value
                    ? config.padding
                    : 0,
            height: rect.height,
        } as ViewStyle;
    }, [rect.width, rect.height, rect.x, config]);

    const renderContent = () =>
        Platform.isTV ? (
            <NormalCellTV {...props} onFocus={onSelect} onPress={onPressTV} />
        ) : (
            <NormalCellMobile {...props} onPress={onSelect} />
        );

    return config.stickyPrograms ? (
        <Animated.View style={animatedStyle}>
            {/* Masking for rounded corners on the right side of the sticky program */}
            {renderContent()}
            <StickyCellBorderMasking config={config} />
        </Animated.View>
    ) : (
        renderContent()
    );
};

export default memo(NormalCell);
